"use client";

import React from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DropdownMenuHeaderProps {
  title: string;
  onBack?: () => void;
  onClose?: () => void;
  className?: string;
  showBack?: boolean;
  showClose?: boolean;
}

export function DropdownMenuHeader({
  title,
  onBack,
  onClose,
  className,
  showBack = true,
  showClose = true,
}: DropdownMenuHeaderProps) {
  return (
    <div className={cn("flex-shrink-0", className)} draggable={false}>
      {/* `relative` lets the title sit absolutely-centered on mobile so it's
          visually centered within the sheet header regardless of how much
          horizontal space the back/close buttons consume. On desktop the
          title reverts to the original inline, left-aligned compact label. */}
      <div className="relative flex items-center pl-3 pr-3 pt-4 pb-3 md:pt-[13px] md:pb-[9px]">
        {/* Back button */}
        {showBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="inline-flex items-center justify-center rounded-md hover-component transition-colors duration-75 flex-shrink-0 mr-2 relative z-10"
            style={{ height: '22px', width: '24px', padding: '0px' }}
          >
            <ArrowLeft className="w-4 h-4 text-sec" />
          </button>
        )}

        {/* Heading — on mobile, absolute-centered + larger/bolder to match the
            action-menu style from the page-more-settings sheet. On desktop,
            keeps the original compact small-caps look. */}
        <span
          className={cn(
            "truncate text-sec",
            // Mobile (default): centered within the header.
            "absolute left-1/2 -translate-x-1/2 max-w-[60%] text-center text-base font-semibold",
            // Desktop (≥ md): inline, left-aligned, compact label. 
            "md:static md:left-auto md:translate-x-0 md:min-w-0 md:max-w-none md:flex-1 md:text-left md:text-xs md:font-medium",
          )}
        >
          {title}
        </span>

        {/* Mobile-only spacer that pushes the close button to the right edge,
            since the absolutely-positioned title doesn't consume horizontal
            space. Hidden on desktop where `flex-1` on the title takes over. */}
        <div className="flex-1 md:hidden" aria-hidden="true" />

        {/* Close button */}
        {showClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded-full modal-action-hover transition-colors duration-75 flex-shrink-0 relative z-10"
          >
            <X className="w-3.5 h-3.5 text-sec" />
          </button>
        )}
      </div>
    </div>
  );
}

